import {
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { safeApiCall } from "api/common.service";
import { getDebtorBackOrders } from "api/debtor.service";
import { formatDate } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import { DefaultTableLayout } from "pages/admin/dataEntry/TableGridDefinitions";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginatedDebtorBackOrders } from "types/model.types";

const AdminDebtorBackOrder: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const [activePage, setActivePage] = useState(1);
  const [loading, setloading] = useState<boolean>(false);
  const [backOrders, setBackOrders] = useState<PaginatedDebtorBackOrders>({
    data: [],
    total: 1,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  useEffect(() => {
    if (!id) return;

    const numeric = Number(id);
    const isNumeric = !Number.isNaN(numeric);

    if (!isNumeric) {
      setBackOrders({
        data: [],
        total: 1,
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      return;
    }

    setRecordId(numeric);
    if (numeric !== recordId) setActivePage(1);
  }, [id]);

  useEffect(() => {
    if (!recordId) return;

    let mounted = true;
    setloading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getDebtorBackOrders(Number(id), activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted && response) {
          setBackOrders(response);
        }
      } finally {
        setloading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [activePage, recordId]);

  const rows = backOrders.data.map((item) => {
    return (
      <Table.Tr key={item.id}>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td>{`${item.productId}${(item.productCode ?? "").toUpperCase()}`}</Table.Td>
        <Table.Td className="notes">{item.productDescription}</Table.Td>
        <Table.Td className="number">{item.quantity}</Table.Td>
        <Table.Td>{item.reference}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack mt={10}>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              disabled={loading}
              total={Math.ceil(backOrders.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
          <DefaultTableLayout>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date Ordered</Table.Th>
                <Table.Th>Product Code</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th className="money">Quantity</Table.Th>
                <Table.Th className="money">Reference</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </DefaultTableLayout>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              disabled={loading}
              total={Math.ceil(backOrders.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
    </Stack>
  );
};

export default AdminDebtorBackOrder;
